import React, { useEffect, useState } from "react";
import { Leaf, Shield, Truck, Star, Users, Award, CheckCircle, ArrowRight, Heart, Globe } from "lucide-react";

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Quality Guaranteed",
      description: "Premium materials and rigorous testing ensure durability"
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "100% Eco-Friendly",
      description: "Made from recyclable materials, completely sustainable"
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Fast Delivery",
      description: "Quick turnaround times for all bulk orders"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Custom Branding",
      description: "Personalize with your logo and brand colors"
    }
  ];

  const stats = [
    { number: "1000+", label: "Happy Customers" },
    { number: "50K+", label: "Bags Delivered" },
    { number: "5★", label: "Average Rating" },
    { number: "100%", label: "Eco-Friendly" }
  ];

  return (
    <section className="relative w-full min-h-screen overflow-hidden">
      {/* Background Image with Enhanced Overlay */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="images/sac.jpg"
          alt="Eco-friendly bags background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/80 via-emerald-900/70 to-teal-900/80"></div>
        <div className="absolute inset-0 bg-black/20"></div>
      </div>

      {/* Floating Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-20 lg:py-32">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm font-medium mb-8">
              <Heart className="w-4 h-4" />
              <span>About Our Mission</span>
            </div>
            
            <h2 className="text-5xl lg:text-7xl font-bold mb-8 text-white">
              About
              <span className="block bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                GREEN ECO
              </span>
            </h2>
            
            <div className="w-32 h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full mx-auto mb-8"></div>
            
            <p className="text-xl lg:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              We're passionate about creating sustainable solutions that help businesses 
              reduce their environmental impact while maintaining quality and style.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Left Content */}
            <div className={`space-y-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  Eco-Friendly Non-Woven Bags for Businesses
                </h3>
                
                <p className="text-lg text-white/90 mb-8 leading-relaxed">
                  Looking for a sustainable and cost-effective packaging solution? 
                  Our high-quality non-woven bags help businesses reduce plastic waste 
                  while maintaining durability and style.
                </p>

                <div className="space-y-4">
                  {[
                    "Affordable & High-Quality – Priced between $0.14 to $0.22 per bag",
                    "Eco-Friendly & Reusable – Made from recyclable materials",
                    "Durable & Lightweight – Strong enough for heavy loads",
                    "Customizable Branding – Available for bulk orders with custom printing"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-lg">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-green-500/20 rounded-xl border border-green-400/30">
                  <p className="text-green-100 font-semibold text-lg">
                    Order in bulk today and save while making a difference!
                  </p>
                </div>
              </div>
            </div>

            {/* Right Content - Features Grid */}
            <div className={`grid grid-cols-2 gap-6 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105"
                  style={{
                    animationDelay: `${index * 150}ms`,
                    animation: 'fadeInUp 0.6s ease-out forwards'
                  }}
                >
                  <div className="text-green-400 mb-4">
                    {feature.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-white/80 text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Section */}
          <div className={`bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 mb-16 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">Our Impact</h3>
              <p className="text-white/80 text-lg">Making a difference, one bag at a time</p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-green-400 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-white/80 text-lg">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className={`text-center transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                <div className="flex items-center space-x-3 relative z-10">
                  <span>Learn More</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </button>

              <button className="group border-2 border-white text-white hover:bg-white hover:text-green-600 px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-3">
                <Globe className="w-5 h-5" />
                <span>Our Mission</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default AboutUs;
